<template>
  <span 
    v-if="iconExists" 
    class="inline-flex items-center justify-center"
    :class="[
      sizeClasses[size as keyof typeof sizeClasses],
      colorClass,
      className
    ]"
    aria-hidden="true"
  >
    <svg class="icon-svg w-full h-full" :class="{ 'icon-inherit-color': forceInheritColor }">
      <use :href="`${spriteUrl}#${iconName}`" />
    </svg>
  </span>
  <span 
    v-else
    class="inline-flex items-center justify-center"
    :class="[
      sizeClasses[size as keyof typeof sizeClasses],
      colorClass,
      className
    ]"
    aria-hidden="true"
  >
    <!-- Empty placeholder when icon not found -->
  </span>
</template>

<script setup lang="ts">
import { logger, createLogger } from '@/utils/logger'

// Create a namespaced logger
const fileLogger = createLogger('UIIcon')

import type { ElementSizeType } from '@/types'
import { computed, onMounted, ref } from 'vue'

// Get the base URL for the sprite
const spriteUrl = import.meta.env.BASE_URL ? `${import.meta.env.BASE_URL}sprite.svg` : '/sprite.svg'

// Custom icon name mapping
const customIconMap: Record<string, string> = {
  'play': 'play',
  'pause': 'pause',
  'stop': 'pause',
  'play-simple': 'play_floating',
  'pause-simple': 'pause_floating',
  'home': 'home',
  'settings': 'settings',
  'search': 'search',
  'bookmark': 'bookmark',
  'close': 'close',
  'library': 'library',
  'menu': 'menu',
  'share': 'share',
  'dots-vertical': 'dots_vertical',
  'forward': 'forward',
  'backward': 'backward',
  'heart-outline': 'like_outline',
  'heart-solid': 'like',
  'timer': 'timer',
  'subscribe': 'subscribe',
  'subscribe-outline': 'subscribe_outline',
  'shopping-bag': 'shop',
  'chevron-down': 'arrow_down',
  'chevron-left': 'arrow_back',
  'list': 'lines',
  'user': 'account',
  'clock': 'history',
  'question-mark': 'help',
  'logout': 'logout',
  'download': 'download',
  'previous': 'skip_back',
  'refresh': 'refresh',
  'next': 'skip',
  'music': 'headphones',
  'headphones': 'headphones',
  'star': 'star',
  'minus-circle': 'minus_circle',
  'chevron-right': 'arrow',
  'x-circle': 'close',
  'check-circle': 'check_circle',
  'eye': 'eye',
  'eye-off': 'eye_off',
  'ellipsis-vertical': 'dots_vertical',
  'volume': 'volume',
  'volume-off': 'volume_off',
  'shopping-cart': 'shop',
  'arrows-pointing-out': 'arrows_pointing_out',
  'backward-15': 'backward',
  'forward-15': 'forward',
  'key': 'key',
  'lock': 'lock_tracklist',
  'premium': 'premium',
  'camera': 'camera',
  'sort': 'sort',
  'sort-desc': 'sort_desc',
  'trash': 'trash',
  'minimise': 'minimise',
  'maximise': 'maximise',
}

// Color mapping for common colors
const colorMapping: Record<string, string> = {
  'primary': 'text-primary',
  'secondary': 'text-secondary',
  'white': 'text-white',
  'black': 'text-black',
  'gray': 'text-gray-500',
  'red': 'text-red-500',
  'green': 'text-green-500',
  'blue': 'text-blue-500',
  'yellow': 'text-yellow-500',
  'orange': 'text-orange-500',
  'purple': 'text-purple-500',
  'pink': 'text-pink-500',
  'indigo': 'text-indigo-500',
  'teal': 'text-teal-500',
  'cyan': 'text-cyan-500',
  'lime': 'text-lime-500',
  'amber': 'text-amber-500',
  'emerald': 'text-emerald-500',
  'rose': 'text-rose-500',
  'sky': 'text-sky-500',
  'slate': 'text-slate-500',
  'zinc': 'text-zinc-500',
  'neutral': 'text-neutral-500',
  'stone': 'text-stone-500',
  'current': 'text-current',
}

const props = withDefaults(defineProps<{
  name: string
  size?: ElementSizeType
  color?: string
  className?: string
  forceInheritColor?: boolean
}>(), {
  size: 'md',
  color: 'current',
  className: '',
  forceInheritColor: false
})

const sizeClasses = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
  xl: 'w-8 h-8',
  '2xl': 'w-10 h-10',
  '3xl': 'w-12 h-12',
  '4xl': 'w-14 h-14'
} as const

const iconExists = ref(true)

// Compute the color class based on the color prop
const colorClass = computed(() => {
  // If it's already a Tailwind class like 'text-red-500', return it directly
  if (props.color.startsWith('text-')) {
    return props.color
  }
  
  // Check if it's in our color mapping
  if (props.color in colorMapping) {
    return colorMapping[props.color]
  }
  
  // If not found, return text-current (inherits color from parent)
  return 'text-current'
})

// Get the icon name with appropriate mapping
const iconName = computed(() => {
  const mappedName = customIconMap[props.name] || props.name;
  // Ensure we use the same safe ID format as in the sprite generator
  return mappedName.replace(/[^a-z0-9_-]/gi, '_');
})
</script>

<style scoped>
.icon-svg {
  display: block;
  color: inherit;
  fill: currentColor;
  stroke: currentColor;
}

/* Force all paths to inherit colors from parent for standard icons */
:deep(svg path:not([fill^="#"])),
:deep(svg rect:not([fill^="#"])),
:deep(svg circle:not([fill^="#"])),
:deep(svg line:not([fill^="#"])),
:deep(svg polyline:not([fill^="#"])),
:deep(svg polygon:not([fill^="#"])) {
  fill: currentColor;
}

:deep(svg path:not([stroke^="#"])),
:deep(svg rect:not([stroke^="#"])),
:deep(svg circle:not([stroke^="#"])),
:deep(svg line:not([stroke^="#"])),
:deep(svg polyline:not([stroke^="#"])),
:deep(svg polygon:not([stroke^="#"])) {
  stroke: currentColor;
}

.icon-inherit-color :deep(path),
.icon-inherit-color :deep(rect),
.icon-inherit-color :deep(circle),
.icon-inherit-color :deep(line),
.icon-inherit-color :deep(polyline),
.icon-inherit-color :deep(polygon) {
  fill: currentColor !important;
  stroke: currentColor !important;
}
</style> 